import type { FC, ReactNode } from 'react';

/* eslint-disable react/jsx-no-useless-fragment */
import { OverlayPacmanLoader } from '@coop/components';
import { useEffect } from 'react';

import type { EpiSitePageType } from '../pages/epiSitePageType';

import ComponentPortals from '../../../common/components/molecules/ComponentPortals';
import { useGroupError } from '../../../common/hooks/useGroupError';
import { ErrorGroup } from '../../../common/store/structureDefinitions/errorsState';
import { useCurrentEpiPage } from '../hooks/useCurrentEpiPage';
import useSimpleAddressRedirect from '../hooks/useSimpleAddressRedirect';
import EpiComponentLoader from './EpiComponentLoader';

interface PageLoaderProps {
    portalName?: string;
    onPageLoad?: (page: EpiSitePageType) => void;
    depth?: number;
    children: (page: EpiSitePageType) => ReactNode;
    useLoader?: boolean;
    /** if we have wildcard matches, we should only load the baseroute for the page. Dont include wildcard matches in url when loading from content delivery api */
    useBaseRoute?: boolean;
}

type EpiPageLoaderProps = Omit<PageLoaderProps, 'children'>;

const PageLoaderByUrl: FC<PageLoaderProps> = ({
    portalName,
    onPageLoad,
    children,
    useLoader = true,
    useBaseRoute = false,
}) => {
    const {
        data: page,
        error,
        isLoading,
        isFetching,
    } = useCurrentEpiPage<EpiSitePageType>({ depth: 5 }, useBaseRoute);

    useEffect(() => {
        if (page) {
            onPageLoad?.(page);
        }
    }, [page, onPageLoad]);

    useGroupError(ErrorGroup.Global, error || undefined);

    const isLoadingFinal = isLoading || isFetching;
    const redirectTo = useSimpleAddressRedirect({
        pageUrl: page?.url,
        simpleAddressUrl: page?.simpleAddressUrl,
    });

    if (redirectTo) {
        // we still need to do hard redirect to ensure proper layout, this can be changed to Navigate if we only have one main Router
        return <HardRedirect to={redirectTo} />;
    }

    if (portalName) {
        return (
            <>
                {page && (
                    <>
                        <ComponentPortals
                            cmpName={portalName}
                            render={() => (
                                <>
                                    {children(page)}
                                    {useLoader && (
                                        <EpiPageLoaderIndicator isLoading={isLoadingFinal} />
                                    )}
                                </>
                            )}
                        />
                    </>
                )}
            </>
        );
    }

    if (page) {
        return (
            <>
                {children(page)}
                {useLoader && <EpiPageLoaderIndicator isLoading={isLoadingFinal} />}
            </>
        );
    }

    return useLoader ? <EpiPageLoaderIndicator isLoading={isLoadingFinal} /> : null;
};

const EpiPageLoader: FC<EpiPageLoaderProps> = ({
    portalName,
    onPageLoad,
    depth,
    useLoader = true,
    useBaseRoute = false,
}) => {
    return (
        <PageLoaderByUrl
            portalName={portalName}
            onPageLoad={onPageLoad}
            depth={depth}
            useLoader={useLoader}
            useBaseRoute={useBaseRoute}
        >
            {(page) => (
                <>
                    <EpiComponentLoader
                        expandedValue={page}
                        fallback={useLoader && <EpiPageLoaderIndicator />}
                    />
                </>
            )}
        </PageLoaderByUrl>
    );
};

const EpiPageLoaderIndicator = ({ isLoading }: { isLoading?: boolean }) => {
    return <OverlayPacmanLoader delay={400} isLoading={!!isLoading} />;
};

const HardRedirect = ({ to }: { to: string }) => {
    useEffect(() => {
        window.location.replace(to);
    }, [to]);

    return null;
};

export default EpiPageLoader;
